import Vue from "vue";
import Vuex from "vuex";
import VuexPersistence from "vuex-persist";
import theme from "./theme.module";
import snackbar from "./snackbar.module";
import table from "./table.module";
import category from "./category.module";
import product from "./product.module";
import cart from "./cart.module";
import session from "./session.module";
import payment from "./payment.module";
import paymentMethod from "./paymentMethod.module";
import auth from "./auth.module";
// TEMPORARY (see src/debug/): reports a storage failure on screen for phone testing.
import { debugError } from "../debug/errorOverlay";

Vue.use(Vuex);

/**
 * Persisting the entire store put ~3 MB into localStorage -- product.groupedData
 * alone was ~2.9 MB of it. iOS Safari caps localStorage near 5 MB per origin, so
 * every setItem began throwing QuotaExceededError, including the write triggered
 * during add-to-cart. That threw before `this.dialog = false` could run, which is
 * why the dialog stopped closing on iPhone but not on desktop.
 *
 * Persist only what has to survive a reload. Everything else is refetched:
 *   product       - refetched by Menu.vue on mount; the source of the overflow
 *   category      - refetched by Menu.vue on mount
 *   table         - fetched by TableAutoComplete when a table is chosen
 *   payment,
 *   paymentMethod - fetched by the payment pages on mount
 *   auth          - the token lives in its own localStorage keys, written by
 *                   SET_TOKEN and read directly by timer.js and plugins/axios.js,
 *                   so the vuex copy is redundant
 *   snackbar      - transient toast state; persisting it resurrects a stale
 *                   message (e.g. an old error banner) after every reload
 *
 * theme is kept because PMain gates the whole app render on it, so without a
 * cached copy every reload shows a blank screen until settings arrive.
 */
const PERSISTED_MODULES = ["cart", "session", "theme"];

/**
 * The order POST response is the whole order graph -- rounds, line items, and a
 * full product record per line. 253 kB on the test tenant, 458 kB in production,
 * and it grows with every round added. SET_ORDER_DATA puts that in
 * state.cart.order, so it was being re-stringified into localStorage on every
 * cart mutation; that serialisation is what made placing an order take ~10 s on
 * an iPhone. Same shape of problem as the groupedData overflow above, slow
 * rather than fatal because it still fits under the quota.
 *
 * Nothing needs the graph to survive a reload. Every page that renders it --
 * MyOrder, PaymentOrderRestaurant, PaymentOrderPickup -- refetches it in
 * mounted(), and each of those refetches is guarded on order.id. So `id` is the
 * only field that genuinely has to persist. The other scalars below are kept
 * only so those pages have something to show in the gap between a reload and
 * the refetch resolving, instead of flashing empty.
 *
 * Deliberately dropped:
 *   order_rounds - the bulk of the payload. Written back as [] rather than
 *                  omitted, because MyOrder renders order_rounds.length before
 *                  its refetch lands.
 *   table        - PaymentOrderRestaurant blocks payment when
 *                  table.active_session is null. That is a staleness check;
 *                  answering it from a cached copy is worse than leaving it
 *                  undefined until the refetch, which that page already guards
 *                  for.
 *
 * This shapes only what is WRITTEN. state.cart.order stays whole in memory, so
 * nothing changes for a page rendering it during the session.
 */
const PERSISTED_ORDER_FIELDS = [
  "id",
  "is_paid",
  "total_price",
  "daily_order_number",
  "order_remark",
];

function persistableOrder(order) {
  if (!order || typeof order !== "object") {
    return order;
  }

  const slim = PERSISTED_ORDER_FIELDS.reduce((acc, field) => {
    if (order[field] !== undefined) {
      acc[field] = order[field];
    }
    return acc;
  }, {});

  if (Array.isArray(order.order_rounds)) {
    slim.order_rounds = [];
  }

  return slim;
}

function pickPersisted(source) {
  const picked = PERSISTED_MODULES.reduce((acc, name) => {
    if (source && source[name] !== undefined) {
      acc[name] = source[name];
    }
    return acc;
  }, {});

  if (picked.cart) {
    // isOrderLoading describes a request in this page's memory. Persisted, a
    // reload mid-order restored it as true with nothing left to clear it, and
    // the Order button stayed disabled. Dropped here it reloads as the
    // module default (false), and restoreState drops a stale true too.
    const { isOrderLoading, ...cartState } = picked.cart;
    picked.cart = { ...cartState, order: persistableOrder(picked.cart.order) };
  }

  return picked;
}

const vuexLocal = new VuexPersistence({
  storage: window.localStorage,
  // vuex-persist uses `reducer` INSTEAD of `modules` when both are given, so the
  // module filtering that used to live in `modules` now runs inside
  // pickPersisted(), alongside the order trimming.
  reducer: pickPersisted,
  // The reducer only filters what is WRITTEN. Restore reads the whole key, so a
  // phone upgrading from a build that persisted everything would otherwise pull
  // the 3 MB product payload -- and a stale snackbar, and the full order --
  // straight back into memory. Filter on the way in as well.
  restoreState: (key, storage) => {
    let saved = {};
    try {
      saved = JSON.parse(storage.getItem(key) || "{}");
    } catch (e) {
      saved = {};
    }
    return pickPersisted(saved);
  },
  // Belt and braces: the module list above is the real fix, but a storage
  // failure must never be able to break ordering again. Drop the stale value
  // and retry once (an older build may have left a large blob behind), then
  // give up quietly rather than throwing into the mutation that triggered it.
  saveState: (key, state, storage) => {
    const payload = JSON.stringify(state);
    try {
      storage.setItem(key, payload);
    } catch (e) {
      try {
        storage.removeItem(key);
        storage.setItem(key, payload);
      } catch (e2) {
        debugError("vuex-persist could not save state (storage full?)", e2);
        if (typeof console !== "undefined" && console.warn) {
          console.warn("vuex-persist: state not saved", e2);
        }
      }
    }
  },
});

export default new Vuex.Store({
  state: {},
  plugins: [vuexLocal.plugin],
  modules: {
    auth,
    theme,
    snackbar,
    table,
    category,
    product,
    cart,
    session,
    payment,
    paymentMethod,
  },
});
